import { isAxiosError } from 'axios'

export type DashboardErrorDetails = {
	status?: number
	statusText?: string
	message?: string
	code?: string
}

/** Extract HTTP error details from Axios/fetch errors */
export function extractErrorDetails(error: unknown): DashboardErrorDetails {
	if (isAxiosError(error)) {
		// Axios sets `response` only when one actually arrived, so a request that
		// never got there (network down, CORS, timeout) carries its reason on the
		// error itself. Reading only `response` here loses it.
		if (!error.response) {
			return { message: error.message, code: error.code }
		}

		const data = error.response.data

		return {
			status: error.response.status,
			statusText: error.response.statusText,
			message: typeof data === 'object' && data !== null && 'message' in data
				? String(data.message)
				: undefined,
		}
	}

	if (error instanceof Error) {
		return {
			message: error.message,
			code: 'code' in error ? String(error.code) : undefined,
		}
	}

	// A rejected literal, or an Error that lost its prototype crossing a
	// serialization boundary, still carries a usable message.
	if (error && typeof error === 'object' && 'message' in error) {
		return {
			message: typeof error.message === 'string' ? error.message : String(error.message),
			code: 'code' in error ? String(error.code) : undefined,
		}
	}

	if (!error || typeof error !== 'object') {
		return { message: String(error) }
	}

	return {}
}

/** Get user-friendly error message based on error details */
export function getErrorMessage(error: unknown): string {
	const details = extractErrorDetails(error)

	if (details.status === 401) {
		return 'Session expired. Please refresh the page to log in again.'
	}
	if (details.status === 403) {
		return "You don't have permission to view these campaigns."
	}
	if (details.status === 404) {
		return 'Campaigns not found. The organization may have been removed.'
	}
	if (details.status && details.status >= 500) {
		return 'Our servers are having trouble. Please try again in a moment.'
	}
	if (details.code === 'ERR_NETWORK' || details.message?.includes('Network')) {
		return 'Network error. Please check your internet connection.'
	}

	return 'Unable to load campaigns. Please try refreshing the page.'
}
